import React, { useState, useEffect, useMemo } from 'react';
import { Col, Row, Button, Drawer, Modal, Dropdown, Menu, Spin, Alert, Space } from 'antd';
import StringFormatter from '../../shared/stringFormatter';
import { useSelector, useDispatch } from 'react-redux';
import * as actionCreators from "../../store/actions/index";
import ComplexDataLinkCard from '../ComplexDataLinkCard/ComplexDataLinkCard';
import SimpleDataLinkCard from "../../components/SimpleDataLinkCard/SimpleDataLinkCard";
import ComplexCard from '../ComplexCard/ComplexCard';
import DataContactInfoRow from '../DataContactInfoRow/DataContactInfoRow';
import DataPhoneNumberRow from '../DataPhoneNumberRow/DataPhoneNumberRow';
import DataAddressRow from '../DataAddressRow/DataAddressRow';
import { DownOutlined, PaperClipOutlined, ControlOutlined } from '@ant-design/icons';
import DocumentList from "../../components/DocumentList/DocumentList";
import EditAccount from "../../components/EditAccount/EditAccount";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTruck, faUsers } from '@fortawesome/free-solid-svg-icons';
import { isBooleanFalse, isListNotEmpty, isNumberNotEmpty, isObjectNotEmpty, isStringNotEmpty } from '../../shared/objectUtils';
import { CanDo, CanSee } from '../../shared/entitlements/entitlements';
import DataRow from '../DataRow/DataRow';
import LoadsMap from '../LoadsMap/LoadsMap';
import axios from 'axios';
import * as Data from '../../api/data';
import Header from '../../components/Header/Header';
import logger from '../../shared/logger';
import AccountUtils from '../../api/utils/accountUtils';
import EditMissingAccountInformation from '../EditMissingAccountInformation/EditMissingAccountInformation';

const stringFormatter = new StringFormatter();

const CompanyCarrierSnapshot = (props) => {
    const fullWidth = global.window.innerWidth;

    const [metrics, setMetrics] = useState(null);
    const [showEditEntity, setShowEditEntity] = useState(false);
    const [showDocumentsModal, setShowDocumentsModal] = useState(false);
    const [missingDocumentsCount, setMissingDocumentsCount] = useState(0);
    const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
    const [imageUrl, setImageUrl] = useState(null);
    const [missingAccountInformation, setMissingAccountInformation] = useState([]);
    const [showEditMissingAccountInformation, setShowEditMissingAccountInformation] = useState([]);
    const [selectedMissingAccountInformation, setSelectedMissingAccountInformation] = useState(null);

    const dispatch = useDispatch();
    const entityId = useSelector(state => state.auth.entityId);
    const entityType = useSelector(state => state.auth.entityType);
    const isAccountLoading = useSelector(state => state.accounts.isRecordLoading);
    const account = useSelector(state => state.accounts.record);
    const isLoading = useSelector(state => state.metrics.metricsIsLoading);
    const allMetrics = useSelector(state => state.metrics.metrics);

    const verifyAccount = (accountId) => {
        dispatch(actionCreators.verifyAccount(accountId));
    };

    const toggleEditEntity = () => {
        setShowEditEntity(!showEditEntity);
    };

    const toggleDocumentsModal = () => {
        setShowDocumentsModal(!showDocumentsModal);
    };

    const toggleEditMissingAccountInformation = () => {
        setShowEditMissingAccountInformation(!showEditMissingAccountInformation);
    };

    const menu = () => {
        return (
            <Menu>
                <Menu.Item key="manageDocuments" onClick={(e) => { e.domEvent.stopPropagation(); toggleDocumentsModal(); }}>
                    <PaperClipOutlined style={{ marginRight: 8 }} />
                    <span>Manage Documents</span>
                </Menu.Item>
                <Menu.Item key="editAccount" onClick={(e) => { e.domEvent.stopPropagation(); toggleEditEntity(); }}>
                    <FontAwesomeIcon className="anticon" icon={faUsers} style={{ marginRight: 8 }} />
                    <span>Edit Account Details</span>
                </Menu.Item>
            </Menu>
        );
    };

    const extra = (
        <Dropdown overlay={menu} trigger={['click']}>
            <Button onClick={(e) => { e.stopPropagation(); }}><ControlOutlined /> <DownOutlined /></Button>
        </Dropdown>
    );

    const editEntityComponents = (
        <Drawer
            title={"Edit Account Details"}
            onClose={toggleEditEntity}
            visible={showEditEntity === true}
            bodyStyle={{ paddingBottom: 80 }}
            style={{ zIndex: 1000 }}
            width={fullWidth > 720 ? fullWidth / 2 : 360}
            destroyOnClose={true}
            closable={false}
            maskClosable={false}
        >
            <EditAccount
                cancel={toggleEditEntity}
                account={account}
            />
        </Drawer>
    );

    const documentsComponent = (
        <Modal
            title="Account Documents"
            visible={showDocumentsModal === true}
            width="100%"
            style={{ top: 0 }}
            onCancel={toggleDocumentsModal}
            footer={null}
            zIndex={1000}
            destroyOnClose={true}
            maskClosable={false}
        >
            {isObjectNotEmpty(account) ? (
                <DocumentList
                    documentEntityType="ACCOUNT"
                    account={account}
                    documentEntityId={account.id}
                    updatePendingApprovalCount={setPendingApprovalCount}
                    updateMissingDocumentsCount={setMissingDocumentsCount}
                />
            ) : null}
            {isObjectNotEmpty(account) && isBooleanFalse(account.isVerified) ? (
                <CanSee entityAction="VERIFY" entityModel="ACCOUNT" entityObject={account}>
                    <Button style={{ marginTop: 20 }} type='primary' onClick={() => { verifyAccount(account.id); }} disabled={pendingApprovalCount > 0 || missingDocumentsCount > 0}>Verify Account</Button>
                </CanSee>
            ) : null}
        </Modal>
    );

    const editMissingAccountInformationDrawer = (
        <Drawer
            title={"Edit Missing Details for " + (isObjectNotEmpty(selectedMissingAccountInformation) ? selectedMissingAccountInformation.entityName : "")}
            onClose={toggleEditMissingAccountInformation}
            visible={showEditMissingAccountInformation === true}
            bodyStyle={{ paddingBottom: 80 }}
            style={{ zIndex: 1000 }}
            width={fullWidth > 720 ? fullWidth / 2 : 360}
            destroyOnClose={true}
            closable={false}
            maskClosable={false}
        >
            <EditMissingAccountInformation
                cancel={toggleEditMissingAccountInformation}
                missingAccountInformation={selectedMissingAccountInformation}
                account={account}
            />
        </Drawer>
    );

    const missingAccountInformationDisplay = () => {
        if (isListNotEmpty(missingAccountInformation)) {
            return (
                <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                    <Col span={24}>
                        {missingAccountInformation.map((missingInfo, index, arr) => {
                            return (
                                <Alert
                                    key={`missing-account-information-${index}`}
                                    style={index < arr.length - 1 ? { marginBottom: 8 } : null}
                                    type="error"
                                    message={`${missingInfo.entityName} is missing ${missingInfo.missingFieldName}`}
                                    showIcon={true}
                                    action={<Space><Button size="small" type="ghost" onClick={(e) => { setSelectedMissingAccountInformation(missingInfo); toggleEditMissingAccountInformation(); }}>Fix</Button></Space>}
                                />
                            );
                        })}
                    </Col>
                </Row>
            );
        } else {
            return null;
        }
    };

    useMemo(() => {
        if (isStringNotEmpty(entityType) && isStringNotEmpty(entityId) && (entityType === "SHIPPER" || entityType === "CARRIER" || entityType === "PRODUCER" || entityType === "RECEIVER" || entityType === "BROKER" || entityType === "FACTORING")) {
            dispatch(actionCreators.fetchAccount(entityId));
        }
    }, [entityType, entityId]);

    useMemo(() => {
        if (isObjectNotEmpty(account) && account.id === entityId) {
            let missingAccountInformationList = AccountUtils.getMissingAccountInformation(account);
            setMissingAccountInformation(missingAccountInformationList);
        }
    }, [account, entityId]);

    useMemo(() => {
        if (isObjectNotEmpty(account) && isStringNotEmpty(account.id)) {
            dispatch(actionCreators.fetchMetrics('CARRIER', account.id));
        }
    }, [account]);

    useMemo(() => {
        if (isObjectNotEmpty(account) && isStringNotEmpty(account.id) && isObjectNotEmpty(allMetrics) && isObjectNotEmpty(allMetrics[account.id])) {
            setMetrics(allMetrics[account.id]);
        }
    }, [allMetrics, account]);

    useEffect(() => {
        const { cancel, token } = axios.CancelToken.source();

        if (isObjectNotEmpty(account) && isStringNotEmpty(account.logoFileId)) {
            Data.getImageByDocumentId(account.logoFileId, { cancelToken: token }).then((data) => {
                const blob = new Blob([data], {
                    type: 'image/png',
                });
                setImageUrl(URL.createObjectURL(blob));
            }).catch(err => {
                if (axios.isCancel(err)) {
                    // console.log('Request cancelled', err.message);
                } else {
                    logger.logDebugEvent('Dashboard.js', err.message, true);
                }
            });
        }

        return () => {
            // console.log("Cleaning up");
            if (cancel !== undefined) {
                cancel('Operation cancelled by the user.');
            }
        };
    }, [account]);

    return (
        <Spin style={{ height: '100%', width: '100%' }} size="large" spinning={isLoading === true || isAccountLoading === true}>
            {isObjectNotEmpty(account) && missingAccountInformation.length > 0 ? (
                missingAccountInformationDisplay()
            ) : null}
            <Header
                title={isObjectNotEmpty(account) && isStringNotEmpty(account.name) ? account.name : 'Loading...'}
                title2={isObjectNotEmpty(account) && isObjectNotEmpty(account.companyAddress) ? stringFormatter.toFormattedAddress(account.companyAddress) : ''}
                subtitle={isObjectNotEmpty(account) && isStringNotEmpty(account.irisId) ? account.irisId : ''}
                logo={imageUrl}
                icon={<FontAwesomeIcon className="anticon" icon={faTruck} />}
                highlights={[
                    {
                        title: 'EIN #',
                        value: isObjectNotEmpty(account) && isStringNotEmpty(account.ein) ? account.ein : null,
                    },
                    {
                        title: 'DOT #',
                        value: isObjectNotEmpty(account) && isStringNotEmpty(account.dotNum) ? account.dotNum : null,
                    },
                    {
                        title: 'MC #',
                        value: isObjectNotEmpty(account) && isStringNotEmpty(account.mcNum) ? account.mcNum : null,
                    },
                    {
                        title: 'SMS Score',
                        value: isObjectNotEmpty(account) && isObjectNotEmpty(account.smsBasic) && isStringNotEmpty(account.smsBasic.weightedAvgSMSScore) ? account.smsBasic.weightedAvgSMSScore : 'N/A'
                    },
                ]}
            />
            {(isObjectNotEmpty(account) && isLoading === false) ? (
                <div style={{ padding: 24 }}>
                    <Row gutter={[24, 24]}>
                        <Col lg={{ span: 12 }} md={{ span: 12 }} sm={{ span: 24 }}>
                            <ComplexCard
                                title="Account Details"
                                headStyle={{ fontSize: 14 }}
                                extra={extra}
                            >
                                {isStringNotEmpty(account.irisId) ? <DataRow title="Account ID" value={account.irisId} dataType="String" /> : null}
                                {isStringNotEmpty(account.email) ? <DataRow title="Email Address" value={account.email} dataType="String" /> : null}
                                {isStringNotEmpty(account.phone) ? <DataPhoneNumberRow title="Phone Number" phone={account.phone} phoneExt={account.phoneExt} /> : null}
                                {isStringNotEmpty(account.afterHoursPhone) ? <DataPhoneNumberRow title="After Hours Phone Number" phone={account.afterHoursPhone} phoneExt={account.afterHoursPhoneExt} /> : null}
                                {isStringNotEmpty(account.fax) ? <DataPhoneNumberRow title="Fax Number" phone={account.fax} /> : null}
                                {isObjectNotEmpty(account.pointOfContact) ? <DataContactInfoRow separateLines={false} valueStyle={{ paddingLeft: 16 }} title="Point of Contact" contact={account.pointOfContact} /> : null}
                                {isObjectNotEmpty(account.companyAddress) ? <DataAddressRow title="Company Address" oneLine={false} valueStyle={{ paddingLeft: 16 }} address={account.companyAddress} /> : null}
                                {CanDo({ adminsOnly: true }) ? (
                                    <>
                                        <DataRow title="Is Active" value={account.isActive} dataType="Boolean" />
                                        <DataRow title="Is Verified" value={account.isVerified} dataType="Boolean" />
                                    </>
                                ) : null}
                            </ComplexCard>
                        </Col>
                        <Col lg={{ span: 12 }} md={{ span: 12 }} sm={{ span: 24 }}>
                            <ComplexCard
                                title="Accounts Receivable"
                                headStyle={{ fontSize: 14 }}
                            >
                                {isObjectNotEmpty(account.accountsReceivable) ? <DataContactInfoRow title="A/R" separateLines={false} valueStyle={{ paddingLeft: 16 }} contact={account.accountsReceivable} /> : null}
                                {isObjectNotEmpty(account.accountsReceivableAddress) ? <DataAddressRow title="A/R Address" oneLine={false} valueStyle={{ paddingLeft: 16 }} address={account.accountsReceivableAddress} /> : null}
                                {isStringNotEmpty(account.preferredPaymentMethod) ? <DataRow title="Preferred Payment Method" value={account.preferredPaymentMethod} dataType="PreferredPaymentMethod" /> : null}
                            </ComplexCard>
                        </Col>
                        <Col lg={{ span: 12 }} md={{ span: 12 }} sm={{ span: 24 }}>
                            <ComplexDataLinkCard
                                title="Dispatch Information"
                                style={{ border: '1px solid #ffe58f' }}
                                headStyle={{ backgroundColor: '#fffbe6', borderBottom: '1px solid #ffe58f', fontSize: 14 }}
                                data={{
                                    "Assets Available": {
                                        value: `${isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.totalAssetsAvailable) ? metrics.totalAssetsAvailable : 0} / ${isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.totalAssets) ? metrics.totalAssets : 0}`,
                                        dataType: 'String',
                                        url: CanDo({ entityTypes: ['CARRIER'] }) ? '/assets' : null,
                                    },
                                    "Drivers Available": {
                                        value: `${isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.totalDriversAvailable) ? metrics.totalDriversAvailable : 0} / ${isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.totalDrivers) ? metrics.totalDrivers : 0}`,
                                        dataType: 'String',
                                        url: CanDo({ entityTypes: ['CARRIER'] }) ? '/drivers' : null,
                                    }
                                }}
                            />
                        </Col>
                        <Col lg={{ span: 12 }} md={{ span: 12 }} sm={{ span: 24 }}>
                            <ComplexDataLinkCard
                                title="Earnings"
                                style={{ border: '1px solid #b7eb8f' }}
                                headStyle={{ backgroundColor: '#f6ffed', borderBottom: '1px solid #b7eb8f', fontSize: 14 }}
                                data={{
                                    "Total Earnings": {
                                        value: isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.totalEarnings) ? Math.abs(metrics.totalEarnings) : 0,
                                        units: isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.totalEarnings) && isStringNotEmpty(metrics.totalEarningsUnit) ? metrics.totalEarningsUnit : null,
                                        dataType: 'Money'
                                    },
                                    "Total Completed Loads": {
                                        value: isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.totalCompletedLoads) ? metrics.totalCompletedLoads : 0,
                                        dataType: 'Integer',
                                        url: CanDo({ entityTypes: ['CARRIER'] }) ? '/completedLoads' : null,
                                    },
                                    "Earnings This Mo": {
                                        value: isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.totalEarningsThisMonth) ? Math.abs(metrics.totalEarningsThisMonth) : 0,
                                        units: isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.totalEarningsThisMonth) && isStringNotEmpty(metrics.totalEarningsThisMonthUnit) ? metrics.totalEarningsThisMonthUnit : null,
                                        dataType: 'Money'
                                    },
                                    "Completed Loads This Mo": {
                                        value: isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.completedLoadsThisMonth) ? metrics.completedLoadsThisMonth : 0,
                                        dataType: 'Integer',
                                        url: CanDo({ entityTypes: ['CARRIER'] }) ? '/completedLoads' : null,
                                    }
                                }}
                            />
                        </Col>
                        <Col lg={{ span: 4 }} md={{ span: 12 }} sm={{ span: 24 }}>
                            <SimpleDataLinkCard
                                style={{ border: '1px solid #ffa39e' }}
                                headStyle={{ backgroundColor: '#fff1f0', borderBottom: '1px solid #ffa39e', fontSize: 14 }}
                                title="Load Requests"
                                dataSize={1}
                                data={isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.totalLoadRequests) ? metrics.totalLoadRequests : 0}
                                url={CanDo({ entityTypes: ['CARRIER'] }) ? '/loadRequests' : null}
                            />
                        </Col>
                        <Col lg={{ span: 4 }} md={{ span: 12 }} sm={{ span: 24 }}>
                            <SimpleDataLinkCard
                                style={{ border: '1px solid #ffa39e' }}
                                headStyle={{ backgroundColor: '#fff1f0', borderBottom: '1px solid #ffa39e', fontSize: 14 }}
                                title="Loads to Dispatch"
                                dataSize={1}
                                data={isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.totalLoadsToDispatch) ? metrics.totalLoadsToDispatch : 0}
                                url={CanDo({ entityTypes: ['CARRIER'] }) ? '/openLoads' : null}
                            />
                        </Col>
                        <Col lg={{ span: 4 }} md={{ span: 12 }} sm={{ span: 12 }}>
                            <SimpleDataLinkCard
                                title="Active Loads"
                                headStyle={{ fontSize: 14 }}
                                dataSize={1}
                                data={isObjectNotEmpty(metrics) && isNumberNotEmpty(metrics.totalActiveLoads) ? stringFormatter.toFormattedDecimal(metrics.totalActiveLoads, 0, null, null) : 0}
                                url={CanDo({ entityTypes: ['CARRIER'] }) ? '/intransitLoads' : null}
                            />
                        </Col>
                        <Col lg={{ span: 6 }} md={{ span: 12 }} sm={{ span: 24 }}>
                            <SimpleDataLinkCard
                                style={{ border: '1px solid #ffa39e' }}
                                headStyle={{ backgroundColor: '#fff1f0', borderBottom: '1px solid #ffa39e', fontSize: 14 }}
                                title="Requests for Proposals"
                                dataSize={3}
                                data={'Coming Soon'}
                                url={CanDo({ entityTypes: ['CARRIER'] }) ? '/requestsForProposals' : null}
                            />
                        </Col>
                        <Col lg={{ span: 6 }} md={{ span: 12 }} sm={{ span: 24 }}>
                            <SimpleDataLinkCard
                                style={{ border: '1px solid #ffa39e' }}
                                headStyle={{ backgroundColor: '#fff1f0', borderBottom: '1px solid #ffa39e', fontSize: 14 }}
                                title="Requests for Bids"
                                dataSize={3}
                                data={'Coming Soon'}
                                url={CanDo({ entityTypes: ['CARRIER'] }) ? '/requestsForBids' : null}
                            />
                        </Col>
                        <Col span={24}>
                            <LoadsMap key={`IN_TRANSIT_MAP_${account.id}`} filter={`IN_TRANSIT_ALL_${account.id}`} carrierId={account.id} showZoomControl={true} containerName="map-all-loads-carrier" />
                        </Col>
                    </Row>
                    {editEntityComponents}
                    {documentsComponent}
                    {editMissingAccountInformationDrawer}
                </div>
            ) : null}
        </Spin>
    );
};

export default CompanyCarrierSnapshot;